import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { PageHeader } from '@/components/layout/PageHeader';
import { StatusBadge } from '@/components/common/StatusBadge';
import { DataTable } from '@/components/common/DataTable';
import { invitationAPI, agencyAPI, agentAPI } from '@/lib/api';
import { isKRRole, formatDate, getRoleDisplayName } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import {
  Mail,
  Plus,
  Copy,
  RefreshCw,
  Trash2,
  Loader2,
  CheckCircle
} from 'lucide-react';

export default function InvitationsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [invitations, setInvitations] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [agents, setAgents] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [copiedToken, setCopiedToken] = useState(null);
  const [invSearch, setInvSearch] = useState('');
  const [invFilters, setInvFilters] = useState({});
  const [formData, setFormData] = useState({
    target_type: 'agency_user',
    email: '',
    agency_id: '',
    npn: '',
    role_to_assign: 'Agency_Principal'
  });

  const isKR = isKRRole(user);

  useEffect(() => {
    if (!isKR) return;
    loadData();
  }, [isKR]);

  const loadData = async () => {
    try {
      const [invRes, agenciesRes, agentsRes] = await Promise.all([
        invitationAPI.list(),
        agencyAPI.list(),
        agentAPI.list({})
      ]);
      setInvitations(invRes.data);
      setAgencies(agenciesRes.data);
      setAgents(agentsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        target_type: formData.target_type,
        email: formData.email,
        agency_id: formData.target_type === 'agency_user' ? formData.agency_id : null,
        npn: formData.target_type === 'agent_user' ? formData.npn : null,
        role_to_assign: formData.target_type === 'agency_user' ? formData.role_to_assign : null
      };
      
      await invitationAPI.create(data);
      toast.success('Invitation created successfully');
      setShowDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    }
  };

  const resetForm = () => {
    setFormData({
      target_type: 'agency_user',
      email: '',
      agency_id: '',
      npn: '',
      role_to_assign: 'Agency_Principal'
    });
  };

  const handleResend = async (invitationId) => {
    try {
      await invitationAPI.resend(invitationId);
      toast.success('Invitation resent (new token generated)');
      loadData();
    } catch (error) {
      toast.error('Failed to resend invitation');
    }
  };

  const handleExpire = async (invitationId) => {
    try {
      await invitationAPI.expire(invitationId);
      toast.success('Invitation expired');
      loadData();
    } catch (error) {
      toast.error('Failed to expire invitation');
    }
  };

  const copyInviteLink = (token) => {
    const link = `${window.location.origin}/login?token=${token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(token);
    toast.success('Invite link copied to clipboard');
    setTimeout(() => setCopiedToken(null), 3000);
  };

  const getAgencyName = (agencyId) => {
    const agency = agencies.find(a => a.agency_id === agencyId);
    return agency?.agency_name || '-';
  };

  const getAgentName = (npn) => {
    const agent = agents.find(a => a.npn === npn);
    return agent ? `${agent.first_name} ${agent.last_name}` : npn;
  };

  const invFilterDefs = useMemo(() => {
    const statuses = [...new Set(invitations.map(i => i.status).filter(Boolean))].sort()
      .map(s => ({ value: s, label: s }));
    const types = [...new Set(invitations.map(i => i.target_type).filter(Boolean))].sort()
      .map(t => ({ value: t, label: t.replace('_', ' ') }));
    return [
      { key: 'status', label: 'Status', options: statuses },
      { key: 'target_type', label: 'Type', options: types },
    ];
  }, [invitations]);

  const invFilterFn = useCallback((row, fv) => {
    if (fv.status && fv.status !== 'all' && row.status !== fv.status) return false;
    if (fv.target_type && fv.target_type !== 'all' && row.target_type !== fv.target_type) return false;
    return true;
  }, []);

  const columns = [
    {
      header: 'Email', accessor: 'email', sortable: true,
      cell: (row) => <span className="font-medium text-slate-900">{row.email}</span>
    },
    {
      header: 'Type', accessor: 'target_type', sortable: true,
      cell: (row) => <span className="capitalize">{row.target_type.replace('_', ' ')}</span>
    },
    {
      header: 'Target',
      cell: (row) => {
        if (row.target_type === 'agency_user') {
          return (
            <div>
              <p>{getAgencyName(row.agency_id)}</p>
              <p className="text-xs text-slate-500">{getRoleDisplayName(row.role_to_assign)}</p>
            </div>
          );
        }
        return <span>Agent: {getAgentName(row.npn)}</span>;
      }
    },
    {
      header: 'Status', accessor: 'status', sortable: true,
      cell: (row) => <StatusBadge status={row.status} />
    },
    {
      header: 'Expires', accessor: 'expires_at', sortable: true,
      cell: (row) => formatDate(row.expires_at)
    },
    {
      header: 'Actions',
      cell: (row) => (
        <div className="flex items-center gap-2">
          {row.status === 'Pending' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyInviteLink(row.token)}
                data-testid={`copy-link-${row.invitation_id}`}
              >
                {copiedToken === row.token ? <CheckCircle size={14} /> : <Copy size={14} />}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleResend(row.invitation_id)}
                data-testid={`resend-${row.invitation_id}`}
              >
                <RefreshCw size={14} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleExpire(row.invitation_id)}
                className="text-red-600 hover:text-red-700"
                data-testid={`expire-${row.invitation_id}`}
              >
                <Trash2 size={14} />
              </Button>
            </>
          )}
        </div>
      )
    }
  ];

  // Group invitations
  const pendingInvitations = invitations.filter(i => i.status === 'Pending');
  const acceptedInvitations = invitations.filter(i => i.status === 'Accepted');
  const expiredInvitations = invitations.filter(i => i.status === 'Expired');

  if (!isKR) {
    return (
      <div className="page-content">
        <Alert>
          <AlertDescription>
            You don't have permission to access this page.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div data-testid="invitations-page">
      <PageHeader 
        title="User Invitations"
        breadcrumbs={[
          { label: 'Dashboard', href: '/dashboard' },
          { label: 'Invitations', href: '/invitations' }
        ]}
        actions={
          <Button onClick={() => setShowDialog(true)} data-testid="create-invitation-btn">
            <Plus size={16} className="mr-2" />
            Create Invitation
          </Button>
        }
      />
      
      <div className="page-content">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <Mail size={20} className="text-slate-400" />
              All Invitations
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
              </div>
            ) : (
              <DataTable
                columns={columns}
                data={invitations}
                emptyTitle="No invitations"
                emptyDescription="Create an invitation to onboard new users."
                searchValue={invSearch}
                onSearchChange={setInvSearch}
                searchPlaceholder="Search by email..."
                searchFields={['email', 'npn', 'target_type']}
                filters={invFilterDefs}
                filterValues={invFilters}
                onFilterChange={(key, val) => setInvFilters(prev => ({...prev, [key]: val}))}
                filterFn={invFilterFn}
                defaultSortKey="created_at"
                defaultSortDir="desc"
                defaultPageSize={25}
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Create Invitation Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Create Invitation</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Invitation Type *</Label>
                <Select
                  value={formData.target_type}
                  onValueChange={(value) => setFormData(prev => ({ 
                    ...prev, 
                    target_type: value,
                    agency_id: '',
                    npn: '',
                    role_to_assign: value === 'agency_user' ? 'Agency_Principal' : ''
                  }))}
                >
                  <SelectTrigger data-testid="type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="agency_user">Agency User</SelectItem>
                    <SelectItem value="agent_user">Agent User</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Email Address *</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="user@example.com"
                  required
                  data-testid="email-input"
                />
              </div>
              
              {formData.target_type === 'agency_user' && (
                <>
                  <div className="space-y-2">
                    <Label>Agency *</Label>
                    <Select
                      value={formData.agency_id}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, agency_id: value }))}
                    >
                      <SelectTrigger data-testid="agency-select">
                        <SelectValue placeholder="Select agency" />
                      </SelectTrigger>
                      <SelectContent>
                        {agencies.map(agency => (
                          <SelectItem key={agency.agency_id} value={agency.agency_id}>
                            {'—'.repeat(agency.depth)} {agency.agency_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label>Role *</Label>
                    <Select
                      value={formData.role_to_assign}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, role_to_assign: value }))}
                    >
                      <SelectTrigger data-testid="role-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="KR_SuperAdmin">KR Super Admin</SelectItem>
                        <SelectItem value="KR_AdminOps">KR Admin Ops</SelectItem>
                        <SelectItem value="Accounting">Accounting</SelectItem>
                        <SelectItem value="Agency_Principal">Agency Principal</SelectItem>
                        <SelectItem value="Agency_User">Agency User</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {formData.target_type === 'agent_user' && (
                <div className="space-y-2">
                  <Label>Agent (NPN) *</Label>
                  <Select
                    value={formData.npn}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, npn: value }))}
                  >
                    <SelectTrigger data-testid="agent-select">
                      <SelectValue placeholder="Select agent" />
                    </SelectTrigger>
                    <SelectContent>
                      {agents.map(agent => (
                        <SelectItem key={agent.npn} value={agent.npn}>
                          {agent.first_name} {agent.last_name} ({agent.npn})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-invitation-btn">
                Create Invitation
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
